/**
 * The user-facing changelog shown at /changelog, newest release first.
 *
 * Adding a release: put a new entry at the top. `version` is the release tag
 * without its "v"; `date` is the tag's date as YYYY-MM-DD. List only what a
 * business user would notice — no refactors, tooling or test changes — and
 * order `changes` by how much each one matters to the user, most first.
 */
export type ChangelogRelease = {
  version: string;
  date: string;
  changes: string[];
};

export const CHANGELOG: ChangelogRelease[] = [
  {
    version: "0.10.0",
    date: "2026-09-29",
    changes: [
      "New Securities page, listing the Security Master's stocks and options.",
      "New Settings page with General, Feature Flags and Cache tabs; the Cache tab can reset the Strategy Report cache.",
      "Strategy Report: buckets now have an inception date, set from the Buckets tab or when a bucket is created, and a new Bucket Inception period reports from it.",
      "Strategy Report: each bucket's summary card shows its weighted time-weighted return, and the Summary gear switches between the two return formulas without re-running the report.",
      "Strategy Report: the Keep card shows the Upside and Downside Participation Rate.",
      "Strategy Report: the Diversify card shows Beginning and Current Concentration, the Holdings count and Bucket 3's market-value return, with a split allocation donut.",
      "Strategy Report: the Tax-Loss Harvest Sleeve shows Bucket 3's performance, net of client deposits and withdrawals, and the days it covers.",
      "Strategy Report: the performance chart was rebuilt — overlay several series at once, including All Securities and Bucket 1's stock-only return — and is now included in the PDF.",
      "Strategy Report: the Net Value scope is measured against the account's margin balance, lists the margin positions behind it and grays out the tiles it does not affect.",
      "Strategy Report: the Excel export adds Performance, Unrealized G/L and Max Drop sheets and shows the working behind the key figures.",
      "Strategy Report: a figure that cannot be calculated now reads N/A with the reason, instead of a misleading number.",
      "Buckets offer only securities the account held during the report period, and mark whether each member is still held.",
      "The Transactions page is in the side menu and can be filtered by transaction type.",
      "The Home page shows each strategy group on its own tab.",
      "Option Chain: the symbol field is now a search picker.",
      "The Orders and Non Optic Positions tables can be sorted by any column.",
      "The Reports page reopens on the tab you last used and remembers the Strategy Report's Period option.",
      "Retired pages are grouped under Obsolete in the side menu and carry a warning.",
      "Removing a position from a strategy now asks for confirmation.",
      "Sign-in shows the reason when it fails.",
      "Fixed the Strategy Report going blank when a figure was missing or a single security had no price.",
      "Fixed page content showing through the header.",
    ],
  },
  {
    version: "0.9.6",
    date: "2026-09-07",
    changes: [
      "Updated the Max Drop calculation.",
      "Margin and deposit/withdrawal transactions are imported with their correct types.",
      "Updated the switch in the Strategy Report's Keep section.",
      "Fixed an Invalid page error in the transactions window when switching accounts.",
    ],
  },
  {
    version: "0.9.5",
    date: "2026-08-21",
    changes: [
      "Strategy Report: new picker for choosing the assets in Buckets 1 and 2.",
      "Strategy Report: review the transactions behind the report before generating it.",
      "Strategy Report: enter the Federal and State tax rates the report uses.",
      "Bucket 3 is calculated from Orion data, and its deposits and withdrawals appear in Transactions and the Excel export.",
      "Reports are generated in the background, so long reports no longer time out.",
      "Option coverage is renamed to Allowed to hedge / Max trade amount.",
      "Fixed the open Trade amount or Portfolio tab not refreshing after Generate history.",
    ],
  },
  {
    version: "0.9.4.1",
    date: "2026-07-21",
    changes: [
      "The max trade amount list is now recalculated automatically every day.",
    ],
  },
  {
    version: "0.9.4",
    date: "2026-07-15",
    changes: [
      "New calculation of the maximum option quantity that can be traded for each stock price change period.",
      "Updated portfolio performance calculations.",
      "Updated the Excel export and fixed several report screen issues.",
    ],
  },
  {
    version: "0.9.3",
    date: "2026-07-10",
    changes: [
      "New Inception button on the report period, covering the strategy from its inception to its removal date.",
      "Missing account histories are generated automatically before a report, with a notification, and can be generated from the report settings.",
      "A strategy can be fully removed from an account with the new clear/delete button on the Client page.",
      "Portfolio values are synced from Orion into the account history.",
      "Corrected stock performance and time-weighted return when the number of shares changes during the period.",
      "Fixed sorting in the transactions window and the chart limits on the report page.",
    ],
  },
  {
    version: "0.9.2",
    date: "2026-06-17",
    changes: [
      "New time-weighted return formula, applied to the portfolio as well.",
      "Total account units are tracked, and dividends are adjusted for them.",
      "Option cash flows are adjusted with a new mechanism.",
      "Values from before the report period no longer distort the report's figures.",
      "Fixed the management window when creating a new strategy.",
    ],
  },
  {
    version: "0.9.1",
    date: "2026-05-27",
    changes: [
      "Dividends are included in the stock's end-of-day value.",
      "Updated the Cumulative Income fields.",
      "Corrected several report calculations.",
    ],
  },
  {
    version: "0.9.0",
    date: "2026-05-21",
    changes: [
      "The Strategy Report shows its calculated figures.",
      "New Tax Savings calculation.",
      "New Max Stock Drop calculation.",
      "Fixed the report's first-day figures.",
    ],
  },
  {
    version: "0.8.12",
    date: "2026-05-05",
    changes: [
      "Corrected the Strategy Report's calculations.",
      "Fixed Download PDF for the Strategy Report.",
      "Fixed the Edit graph window showing no data the first time it opens.",
      "Fixed the Drawdown comparison chart for negative values.",
    ],
  },
];
